import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  /**
   * Updates button states based on current value and limits.
   * Cart buttons are always managed client-side, never server-disabled,
   * except for locked SWF ticket lines: the minus button stays disabled
   * and the plus button stays enabled so its click can be intercepted.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (this.dataset.locked === 'true') {
      minusButton.disabled = true;
      plusButton.disabled = false;
      return;
    }

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Intercept plus-button clicks on locked (SWF ticket) lines: instead of
   * incrementing the existing line, open the attendee modal so the user can
   * enter details for the new ticket. The modal adds a separate line item.
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (this.dataset.locked === 'true') {
      event.preventDefault();
      const row = this.closest('[data-swf-ticket="true"]');
      if (!row) return;
      document.dispatchEvent(new CustomEvent('swf:open-attendee-modal', {
        detail: {
          variantId: row.dataset.swfVariantId,
          productTitle: row.dataset.swfProductTitle,
        },
      }));
      return;
    }
    super.increaseQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
